/* @jsx h */
import { defineComponent, h, ref, computed, watch, onMounted } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useLocalization, PageHeader, type DateRange } from "@lemmonhq/uikit";
import { setTitle } from "@lemmonhq/orders";
import { useTrpc } from "@app/vue/providers/index";

import { ReportSummary } from "../DataRoom/components/ReportSummary";
import { ReportChart } from "../DataRoom/components/ReportChart";
import { ReportTable } from "../DataRoom/components/ReportTable";
import {
  ReservationFilters,
  ReservationFilterPanel,
  buildFilterSummary,
  countActiveFilters,
  chipClass,
  statusLabelKey,
  type ReservationFilterState,
} from "./components/ReservationFilters";
import {
  reservationSummarySpec,
  filteredSummarySpec,
  reservationTableSpec,
  RESERVATION_CHARTS,
  RESERVATION_VIEWS,
  type ReservationView,
} from "./specs";

const ROWS_PAGE_LIMIT = 50;

/** Bare `YYYY-MM-DD`, the format `DateRangePicker` works in. */
function localDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function defaultDateRange(): DateRange {
  const to = new Date();
  const from = new Date();
  // 30 days back rather than a calendar month: reservations are booked ahead,
  // so a month-to-date default would hide most of the pipeline early in a month.
  from.setDate(from.getDate() - 29);
  return { type: "custom", startDate: localDay(from), endDate: localDay(to) };
}

export const ReservationsReportPage = defineComponent({
  name: "ReservationsReportPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();

    const filterState = ref<ReservationFilterState>({
      dateRange: defaultDateRange(),
      statuses: [],
      sources: [],
    });
    const view = ref<ReservationView>("over_time");

    const rows = ref<Array<Record<string, unknown>>>([]);
    const cursor = ref<{ local_time: string; id: string } | null>(null);
    const hasMore = ref(false);
    const loadingRows = ref(false);
    const rowsError = ref<string | null>(null);
    const loadingMore = ref(false);

    onMounted(() => setTitle("Reservations report"));

    /**
     * Local wall-clock `YYYY-MM-DDTHH:mm:ss`, the format the SQL functions
     * expect. The end of a day is 23:59:59, not 23:59 — the SQL range is
     * inclusive at full timestamp granularity, so stopping a minute short would
     * drop bookings in the last minute of the selected day.
     */
    const filters = computed(() => ({
      from: `${filterState.value.dateRange.startDate}T00:00:00`,
      to: `${filterState.value.dateRange.endDate}T23:59:59`,
      ...(filterState.value.statuses.length
        ? { statuses: filterState.value.statuses as never }
        : {}),
      ...(filterState.value.sources.length
        ? { sources: filterState.value.sources as never }
        : {}),
    }));

    const summaryQuery = useQuery({
      queryKey: computed(() => [
        "reservation-analytics",
        "summary",
        filters.value,
      ]),
      queryFn: () => trpc.reservationAnalytics.summary.query(filters.value),
    });

    /**
     * Incremented on every fresh load. A response whose generation is no longer
     * current is discarded rather than written: without this, toggling filters
     * quickly lets a slow earlier response land after a fast later one and
     * repaint the table with data for a filter set nobody selected.
     */
    let generation = 0;

    const loadFirstPage = async () => {
      const mine = ++generation;
      // Cleared synchronously, before the await: any in-flight loadMore is now
      // holding a cursor for a filter set that no longer applies.
      rows.value = [];
      cursor.value = null;
      hasMore.value = false;
      rowsError.value = null;
      loadingRows.value = true;
      try {
        const page = await trpc.reservationAnalytics.rows.query({
          ...filters.value,
          limit: ROWS_PAGE_LIMIT,
        });
        if (mine !== generation) return;
        rows.value = page.data as Array<Record<string, unknown>>;
        cursor.value = page.nextCursor;
        hasMore.value = page.hasMore;
      } catch (e) {
        if (mine !== generation) return;
        // Surfaced rather than swallowed: an unhandled rejection here left the
        // table in its "no rows" state, indistinguishable from an empty period.
        rowsError.value = e instanceof Error ? e.message : String(e);
      } finally {
        if (mine === generation) loadingRows.value = false;
      }
    };

    const loadMore = async () => {
      // loadingRows matters as much as loadingMore: appending a page fetched
      // with the previous filter set's cursor is how duplicate rows appear.
      if (!hasMore.value || loadingMore.value || loadingRows.value) return;
      const mine = generation;
      loadingMore.value = true;
      try {
        const page = await trpc.reservationAnalytics.rows.query({
          ...filters.value,
          limit: ROWS_PAGE_LIMIT,
          cursor: cursor.value,
        });
        if (mine !== generation) return;
        rows.value = [
          ...rows.value,
          ...(page.data as Array<Record<string, unknown>>),
        ];
        cursor.value = page.nextCursor;
        hasMore.value = page.hasMore;
      } catch (e) {
        if (mine !== generation) return;
        rowsError.value = e instanceof Error ? e.message : String(e);
      } finally {
        if (mine === generation) loadingMore.value = false;
      }
    };

    // Any filter change invalidates the cursor — restart from page one rather
    // than paging deeper into a result set that no longer matches.
    watch(filters, () => void loadFirstPage(), { immediate: true, deep: true });

    const WEEKDAYS = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    /**
     * SQL returns raw values only, so the display labels are resolved here:
     * status/source keys become translated text, weekday numbers become names,
     * and hours become "19:00". Keeps translation out of the database.
     */
    const decorated = computed(() => {
      const data = summaryQuery.data.value as Record<string, any> | undefined;
      if (!data) return data;
      const label = (key: string, fallback: string) => {
        const translated = t(key);
        return translated === key ? fallback : translated;
      };
      return {
        ...data,
        by_status: (data.by_status ?? []).map((r: any) => ({
          ...r,
          status_label: label(
            `reports.reservations.status.${String(r.status).replace(/-/g, "_")}`,
            String(r.status),
          ),
        })),
        by_source: (data.by_source ?? []).map((r: any) => ({
          ...r,
          source_label: label(
            `reports.reservations.source.${r.source}`,
            String(r.source),
          ),
        })),
        by_weekday: (data.by_weekday ?? []).map((r: any) => ({
          ...r,
          weekday_label: label(
            `reports.reservations.weekday.${WEEKDAYS[(Number(r.weekday) || 1) - 1]}`,
            String(r.weekday),
          ),
        })),
        by_hour: (data.by_hour ?? []).map((r: any) => ({
          ...r,
          hour_label: `${String(r.hour).padStart(2, "0")}:00`,
        })),
        by_lead_time: (data.by_lead_time ?? []).map((r: any) => ({
          ...r,
          bucket_label: label(
            `reports.reservations.lead.${r.bucket}`,
            String(r.bucket),
          ),
        })),
      };
    });

    const decoratedRows = computed(() =>
      rows.value.map((row) => {
        const status = String(row.status ?? "");
        const source = String(row.source ?? "");
        const label = (key: string, fallback: string) => {
          const translated = t(key);
          return translated === key ? fallback : translated;
        };
        return {
          ...row,
          status_label: label(statusLabelKey(status), status),
          source_label: label(`reports.reservations.source.${source}`, source),
        };
      }),
    );

    /**
     * Desktop-only disclosure state for the chip panel. On mobile the chips
     * live in the header's filter sheet instead, so this never applies there.
     * Read once at setup rather than bound to a resize listener: this is the
     * initial state, and after that it is the user's choice.
     */
    const panelOpen = ref(
      typeof window !== "undefined" && window.innerWidth >= 1024,
    );

    /** Count of non-default filters, so the collapsed state still says what is on. */
    const activeFilterCount = computed(() =>
      countActiveFilters(filterState.value),
    );

    return () => {
      const loading = summaryQuery.isLoading.value;

      const statusFiltered = filterState.value.statuses.length > 0;

      return (
        <div class="flex h-full w-full min-w-0 flex-1 flex-col overflow-hidden">
          {/*
            The shared page header: title left, date range and the chip-panel
            toggle right on desktop; on mobile the title carries a one-line
            summary of what is filtered and everything else moves into the
            filter sheet, the same as the payments and shifts reports.
          */}
          <PageHeader
            title={
              <div class="min-w-0">
                <h1 class="truncate text-xl font-bold text-accent md:text-2xl">
                  {t("reports.reservations.title")}
                </h1>
                {/* Desktop only: on a phone every vertical pixel above the
                    numbers costs a row of the report. */}
                <p class="hidden text-sm text-base-content/70 md:block">
                  {t("reports.reservations.subtitle")}
                </p>
              </div>
            }
            filterSummary={buildFilterSummary(filterState.value, t)}
            activeFilterCount={activeFilterCount.value}
            filterSheetTitle={t("reports.reservations.filters_toggle")}
          >
            {{
              default: ({
                closeMobileSheet,
              }: {
                closeMobileSheet: () => void;
              }) => (
                <ReservationFilters
                  filters={filterState.value}
                  onFiltersChange={(next) => (filterState.value = next)}
                  panelOpen={panelOpen.value}
                  onTogglePanel={() => (panelOpen.value = !panelOpen.value)}
                  onApply={closeMobileSheet}
                />
              ),
            }}
          </PageHeader>

          <div class="min-h-0 flex-1 overflow-y-auto">
            {/* min-w-0 on the column and w-full to cap it: a flex item's default
                min-width is auto, so the wide detail table grew the whole page
                to 693px inside a 375px viewport instead of scrolling inside its
                own overflow-x-auto wrapper. Everything to the right of 375px was
                then silently clipped by an ancestor — which a scrollWidth check
                cannot see, because the page never actually scrolled. */}
            <div class="flex w-full min-w-0 flex-col gap-4 p-3 sm:gap-6 sm:p-4">
              {panelOpen.value && (
                <div class="hidden md:block">
                  <ReservationFilterPanel
                    filters={filterState.value}
                    onFiltersChange={(next) => (filterState.value = next)}
                  />
                </div>
              )}

              {/* The two rates are computed over whatever the status filter left
                  in scope, so filtering to "no-show" would report a 100%
                  no-show rate. True to their definition, but useless as a
                  headline — drop them while a status filter is on, and say why. */}
              {/* A failure has to be visible. Without this the page renders its
                  empty state, which reads as "no bookings in this period" — the
                  opposite of what actually happened. */}
              {(summaryQuery.isError.value || rowsError.value) && (
                <div class="rounded-xl border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-base-content/80">
                  {t("reports.reservations.load_failed")}
                </div>
              )}
              <div class="bg-base-200 p-4 border rounded-xl">
                <ReportSummary
                  spec={
                    statusFiltered ? filteredSummarySpec : reservationSummarySpec
                  }
                  summary={decorated.value}
                  loading={loading}
                />
              </div>
              {statusFiltered && (
                <p class="-mt-3 text-[11px] text-base-content/70">
                  {t("reports.reservations.rates_hidden")}
                </p>
              )}

              <section class="min-w-0 rounded-2xl border  bg-base-100 p-3 shadow-sm sm:p-4">
                {/* The view switch scrolls horizontally rather than wrapping to
                    three rows on a phone. */}
                <div class="-mx-1 mb-3 flex gap-1.5 overflow-x-auto px-1 pb-1 sm:mb-4 sm:flex-wrap sm:overflow-visible">
                  {RESERVATION_VIEWS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={view.value === option.value}
                      class={`${chipClass(view.value === option.value)} shrink-0`}
                      onClick={() => {
                        view.value = option.value;
                      }}
                    >
                      {t(option.labelKey)}
                    </button>
                  ))}
                </div>
                <div class="">
                  <ReportChart
                    spec={RESERVATION_CHARTS[view.value]}
                    summary={decorated.value}
                    loading={loading}
                  />
                </div>
              </section>

              <ReportTable
                spec={reservationTableSpec}
                rows={decoratedRows.value}
                loading={loadingRows.value}
                loadingMore={loadingMore.value}
                hasMore={hasMore.value}
                onLoadMore={loadMore}
              />
            </div>
          </div>
        </div>
      );
    };
  },
});
